/**
 * Module-level toast bus for non-React callers (utility functions,
 * fetch wrappers, etc.) that cannot call the `useToast()` hook.
 * `<ToastBusBridge />`, mounted inside the ui-kit `ToastProvider`,
 * subscribes to this bus and forwards events to the design-system
 * Toast viewport. React components should prefer `useToast()`
 * directly from `@immo/ui-kit`.
 */

export type ToastTone = 'success' | 'error' | 'warning';

export type Toast = {
  readonly id: number;
  readonly tone: ToastTone;
  readonly message: string;
};

type Listener = (toast: Toast) => void;

const listeners = new Set<Listener>();
let nextId = 1;

function emit(tone: ToastTone, message: string): void {
  const next: Toast = { id: nextId++, tone, message };

  for (const listener of listeners) listener(next);
}

export function subscribeToToasts(listener: Listener): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

export const toast = {
  success(message: string): void {
    emit('success', message);
  },
  error(message: string): void {
    emit('error', message);
  },
  warning(message: string): void {
    emit('warning', message);
  },
};
